import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import * as Yup from 'yup'

import { EuiButton, EuiFieldText, EuiFlexGroup, EuiFlexItem, EuiForm, EuiFormRow, EuiSelect, EuiSpacer, EuiText } from '@elastic/eui'
import { EuiSelectOption } from '@elastic/eui/src/components/form/select'

import { phoneRegex } from 'utils/phoneRegex'

import { Account } from '../../api/interfaces'
import { RootState } from '../../app/rootReducer'
import { useAccountForm, UseAccountFormProps } from './useAccountForm'

const defaultAccountSchema = Yup.object().shape({
  businessTypeId: Yup.string(),
  name: Yup.string().max(128).required('Please enter a short company name'),
  streetAddress: Yup.string(),
  city: Yup.string(),
  state: Yup.string(),
  zip: Yup.string(),
  contactPhone: Yup.string().matches(phoneRegex, 'Phone number is not valid'),
  website: Yup.string().url('Please enter a valid URL including https:// (ex. https://www.domain.com)').required('Please enter a website URL')
})

interface ClientDetailsFormProps {
  isNewAccount: boolean
  onSubmit?: (account: Account) => void
  accountSchema?: Yup.ObjectSchema<any>
}

export const ClientDetailsForm = ({ isNewAccount, onSubmit, accountSchema = defaultAccountSchema }: ClientDetailsFormProps) => {
  const history = useHistory()
  const dictionary = useSelector((state: RootState) => state.dictionary)

  const formProps: UseAccountFormProps = { isNewAccount, onSubmit, accountSchema }
  const { formik } = useAccountForm(formProps)

  return (
    <EuiForm component='form' onSubmit={formik.handleSubmit} onChange={formik.handleChange} onBlur={formik.handleBlur}>
      <EuiFormRow label='Company Name *' fullWidth isInvalid={formik.touched.name && !!formik.errors.name} error={formik.errors.name}>
        <EuiFieldText name='name' value={formik.values.name} onChange={formik.handleChange} onBlur={formik.handleBlur} placeholder='' fullWidth isInvalid={formik.touched.name && !!formik.errors.name} />
      </EuiFormRow>
      <EuiFormRow label='Website *' helpText='This is the default website URL that will be used you create new ads' fullWidth isInvalid={formik.touched.website && !!formik.errors.website} error={formik.errors.website}>
        <EuiFieldText name='website' value={formik.values.website} onChange={formik.handleChange} fullWidth isInvalid={formik.touched.website && !!formik.errors.website} />
      </EuiFormRow>
      <EuiFormRow label='Contact First Name' fullWidth isInvalid={formik.touched.contactFirstName && !!formik.errors.contactFirstName} error={formik.errors.contactFirstName}>
        <EuiFieldText name='contactFirstName' value={formik.values.contactFirstName} onChange={formik.handleChange} onBlur={formik.handleBlur} placeholder='' fullWidth isInvalid={formik.touched.contactFirstName && !!formik.errors.contactFirstName} />
      </EuiFormRow>
      <EuiFormRow label={'Contact Last Name'} fullWidth isInvalid={formik.touched.contactLastName && !!formik.errors.contactLastName} error={formik.errors.contactLastName}>
        <EuiFieldText name='contactLastName' value={formik.values.contactLastName} onChange={formik.handleChange} onBlur={formik.handleBlur} placeholder='' fullWidth isInvalid={formik.touched.contactLastName && !!formik.errors.contactLastName} />
      </EuiFormRow>
      <EuiFormRow label='Contact Email' fullWidth isInvalid={formik.touched.contactEmail && !!formik.errors.contactEmail} error={formik.errors.contactEmail}>
        <EuiFieldText name='contactEmail' value={formik.values.contactEmail} onChange={formik.handleChange} onBlur={formik.handleBlur} placeholder='' fullWidth isInvalid={formik.touched.contactEmail && !!formik.errors.contactEmail} />
      </EuiFormRow>
      <EuiFormRow label='Contact Phone' fullWidth isInvalid={formik.touched.contactPhone && !!formik.errors.contactPhone} error={formik.errors.contactPhone}>
        <EuiFieldText name='contactPhone' value={formik.values.contactPhone} onChange={formik.handleChange} onBlur={formik.handleBlur} placeholder='' fullWidth isInvalid={formik.touched.contactPhone && !!formik.errors.contactPhone} />
      </EuiFormRow>
      <EuiFormRow label='Street Address' fullWidth isInvalid={formik.touched.streetAddress && !!formik.errors.streetAddress} error={formik.errors.streetAddress}>
        <EuiFieldText name='streetAddress' value={formik.values.streetAddress} onChange={formik.handleChange} onBlur={formik.handleBlur} placeholder='' fullWidth isInvalid={formik.touched.streetAddress && !!formik.errors.streetAddress} />
      </EuiFormRow>
      <EuiSpacer size='s' />
      <EuiFlexGroup>
        <EuiFlexItem>
          <EuiFormRow label='City' fullWidth isInvalid={formik.touched.city && !!formik.errors.city} error={formik.errors.city}>
            <EuiFieldText name='city' value={formik.values.city} onChange={formik.handleChange} onBlur={formik.handleBlur} placeholder='' fullWidth isInvalid={formik.touched.city && !!formik.errors.city} />
          </EuiFormRow>
        </EuiFlexItem>
        <EuiFlexItem>
          <EuiFormRow label='State' fullWidth isInvalid={formik.touched.state && !!formik.errors.state} error={formik.errors.state}>
            <EuiSelect
              name='state'
              options={[
                {
                  value: '',
                  text: 'Select a state'
                } as EuiSelectOption,
                ...dictionary.states.map(
                  s =>
                    ({
                      value: s.code,
                      text: s.name
                    } as EuiSelectOption)
                )
              ]}
              value={formik.values.state}
              onChange={value => formik.setFieldValue('state', value.target.value, true)}
              onBlur={formik.handleBlur}
              fullWidth
              isInvalid={formik.touched.state && !!formik.errors.state}
            />
          </EuiFormRow>
        </EuiFlexItem>
        <EuiFlexItem>
          <EuiFormRow label='Zip' fullWidth isInvalid={formik.touched.zip && !!formik.errors.zip} error={formik.errors.zip}>
            <EuiFieldText name='zip' value={formik.values.zip} onChange={formik.handleChange} placeholder='' fullWidth isInvalid={formik.touched.zip && !!formik.errors.zip} />
          </EuiFormRow>
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiSpacer />
      <EuiFlexGroup alignItems='center'>
        <EuiFlexItem grow={false}>
          <EuiButton id='save' isDisabled={formik.isSubmitting} onClick={() => history.push('/dashboard')}>
            Cancel
          </EuiButton>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiButton id='save' fill type='submit' isLoading={formik.isSubmitting}>
            Save
          </EuiButton>
        </EuiFlexItem>
        <EuiFlexItem>
          <EuiText size={'xs'}>* required fields</EuiText>
        </EuiFlexItem>
      </EuiFlexGroup>
    </EuiForm>
  )
}
